import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Input, 
  Button, 
  message, 
  Spin, 
  Typography, 
  Space, 
  Divider,
  Row,
  Col,
  Alert,
  Modal,
  Form,
  Badge,
  Tooltip,
  Statistic,
  Flex,
  Progress
} from 'antd';
import { 
  SettingOutlined, 
  ReloadOutlined, 
  SaveOutlined, 
  ExclamationCircleOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function Settings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [config, setConfig] = useState({});
  const [serverStatus, setServerStatus] = useState({});
  const [form] = Form.useForm();
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch current configuration
  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.getConfig();
      setConfig(response.data.config);
      form.setFieldsValue({
        apiToken: '' // Don't pre-fill token for security
      });
    } catch (error) {
      message.error('Error fetching configuration');
    } finally {
      setLoading(false);
    }
  };

  // Fetch server status
  const fetchServerStatus = async () => {
    try {
      const response = await api.getServerStatus();
      setServerStatus(response.data);
    } catch (error) {
      message.error('Failed to fetch server status');
    }
  };

  // Update API token
  const updateToken = async (values) => {
    try {
      setLoading(true);
      const response = await api.updateToken({ token: values.apiToken });
      
      message.success('API Token updated successfully! Server will restart in 3 seconds.');
      form.resetFields(['apiToken']);
      
      // Refresh config after a delay
      setTimeout(() => {
        fetchConfig();
        fetchServerStatus();
      }, 4000);
    } catch (error) {
      message.error(error.response?.data?.message || 'Error updating token');
    } finally {
      setLoading(false);
    }
  };

  // Manual server restart
  const restartServer = () => {
    Modal.confirm({
      title: 'Restart Server',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to restart the server? This will temporarily disconnect all users.',
      okText: 'Yes, Restart',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setRestartLoading(true);
          await api.restartServer('Manual restart from admin panel');
          
          message.success('Server restart initiated! Server will restart in 2 seconds.');
          
          // Refresh status after restart
          setTimeout(() => {
            fetchServerStatus();
          }, 5000);
        } catch (error) {
          message.error(error.response?.data?.message || 'Error restarting server');
        } finally {
          setRestartLoading(false);
        }
      }
    });
  };

  useEffect(() => {
    fetchConfig();
    fetchServerStatus();
    
    // Auto-refresh server status every 30 seconds
    const interval = setInterval(fetchServerStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusBadge = (status) => {
    if (status === 'running') {
      return <Badge status="success" text="Running" />;
    }
    return <Badge status="error" text="Unknown" />;
  };

  if (loading && Object.keys(config).length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      {/* Modern Header */}
      <div className="settings-header" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: isMobile ? '20px' : '24px',
        padding: isMobile ? '24px 20px' : '40px 32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '50%',
          transform: 'translate(50%, -50%)'
        }} />
        
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <div style={{ zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '12px',
                backdropFilter: 'blur(10px)'
              }}>
                <SettingOutlined style={{ fontSize: isMobile ? '24px' : '28px' }} />
              </div>
              <Title level={isMobile ? 3 : 1} style={{ 
                margin: 0, 
                color: 'white', 
                fontSize: isMobile ? '22px' : '32px', 
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                letterSpacing: '-0.5px'
              }}>
                Server Settings
              </Title>
            </div>
            <Text style={{ 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 500
            }}>
              Manage API tokens and server configuration
            </Text>
          </div>
          <Space size={isMobile ? 8 : 12}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchConfig();
                fetchServerStatus();
              }}
              loading={loading}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                borderRadius: '12px',
                height: isMobile ? '40px' : '44px',
                fontWeight: '600'
              }}
            >
              {!isMobile && 'Refresh'}
            </Button>
          </Space>
        </Flex>
      </div>

      {/* Enhanced Statistics Cards */}
      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              height: isMobile ? '100px' : '120px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(82, 196, 26, 0.3)'
            }}
            bodyStyle={{
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <CheckCircleOutlined style={{
              position: 'absolute',
              right: isMobile ? '12px' : '16px',
              top: isMobile ? '12px' : '16px',
              fontSize: isMobile ? '40px' : '60px',
              color: 'rgba(255,255,255,0.2)'
            }} />
            <Statistic
              title={<span style={{ color: 'white', fontSize: isMobile ? '11px' : '14px', fontWeight: 600 }}>Server Status</span>}
              value={serverStatus.status === 'running' ? 'Online' : 'Offline'}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              height: isMobile ? '100px' : '120px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(24, 144, 255, 0.3)'
            }}
            bodyStyle={{
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <ClockCircleOutlined style={{
              position: 'absolute',
              right: isMobile ? '12px' : '16px',
              top: isMobile ? '12px' : '16px',
              fontSize: isMobile ? '40px' : '60px',
              color: 'rgba(255,255,255,0.2)'
            }} />
            <Statistic
              title={<span style={{ color: 'white', fontSize: isMobile ? '11px' : '14px', fontWeight: 600 }}>Uptime</span>}
              value={serverStatus.uptime?.formatted || 'N/A'}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              height: isMobile ? '100px' : '120px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(250, 173, 20, 0.3)'
            }}
            bodyStyle={{
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <ApiOutlined style={{
              position: 'absolute',
              right: isMobile ? '12px' : '16px',
              top: isMobile ? '12px' : '16px',
              fontSize: isMobile ? '40px' : '60px',
              color: 'rgba(255,255,255,0.2)'
            }} />
            <Statistic
              title={<span style={{ color: 'white', fontSize: isMobile ? '11px' : '14px', fontWeight: 600 }}>Memory</span>}
              value={serverStatus.memory?.used || 'N/A'}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={12} md={6} lg={6}>
          <Card
            style={{
              background: config.fullTokenAvailable 
                ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                : 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              height: isMobile ? '100px' : '120px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: config.fullTokenAvailable 
                ? '0 4px 20px rgba(82, 196, 26, 0.3)'
                : '0 4px 20px rgba(255, 77, 79, 0.3)'
            }}
            bodyStyle={{
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <CheckCircleOutlined style={{
              position: 'absolute',
              right: isMobile ? '12px' : '16px',
              top: isMobile ? '12px' : '16px',
              fontSize: isMobile ? '40px' : '60px',
              color: 'rgba(255,255,255,0.2)'
            }} />
            <Statistic
              title={<span style={{ color: 'white', fontSize: isMobile ? '11px' : '14px', fontWeight: 600 }}>API Token</span>}
              value={config.fullTokenAvailable ? 'Active' : 'Missing'}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[isMobile ? 8 : 24, isMobile ? 8 : 24]}>
        {/* Server Status Card */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <ClockCircleOutlined style={{ color: '#1890ff' }} />
                <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600 }}>
                  Server Status
                </span>
              </Space>
            }
            style={{ 
              borderRadius: isMobile ? '16px' : '20px', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0'
            }}
            headStyle={{
              borderBottom: '1px solid #f0f0f0',
              padding: isMobile ? '16px 20px' : '20px 24px'
            }}
            bodyStyle={{
              padding: isMobile ? '20px' : '24px'
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                background: '#f8f9fa',
                borderRadius: '12px'
              }}>
                <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Status:</Text>
                {getStatusBadge(serverStatus.status)}
              </div>
              
              {serverStatus.uptime && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#f8f9fa',
                  borderRadius: '12px'
                }}>
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Uptime:</Text>
                  <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>{serverStatus.uptime.formatted}</Text>
                </div>
              )}
              
              {serverStatus.memory && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#f8f9fa',
                  borderRadius: '12px'
                }}>
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Memory:</Text>
                  <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>{serverStatus.memory.used} / {serverStatus.memory.total}</Text>
                </div>
              )}
              
              {serverStatus.nodeVersion && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#f8f9fa',
                  borderRadius: '12px'
                }}>
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Node Version:</Text>
                  <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>{serverStatus.nodeVersion}</Text>
                </div>
              )}
              
              <Divider style={{ margin: '16px 0' }} />
              
              <Button 
                type="primary" 
                danger 
                icon={<ReloadOutlined />}
                onClick={restartServer}
                loading={restartLoading}
                block
                style={{
                  height: isMobile ? '48px' : '44px',
                  borderRadius: '12px',
                  fontSize: isMobile ? '16px' : '14px',
                  fontWeight: 600
                }}
              >
                🔄 Restart Server
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Current Configuration Card */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <ApiOutlined style={{ color: '#52c41a' }} />
                <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600 }}>
                  Current Configuration
                </span>
              </Space>
            }
            style={{ 
              borderRadius: isMobile ? '16px' : '20px', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0'
            }}
            headStyle={{
              borderBottom: '1px solid #f0f0f0',
              padding: isMobile ? '16px 20px' : '20px 24px'
            }}
            bodyStyle={{
              padding: isMobile ? '20px' : '24px'
            }}
          >
            <Spin spinning={loading}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#f8f9fa',
                  borderRadius: '12px'
                }}>
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>API Key:</Text>
                  <Text code style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    {config.UPSTOX_API_KEY || 'Not set'}
                  </Text>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#f8f9fa',
                  borderRadius: '12px'
                }}>
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>API Token:</Text>
                  <Space>
                    {config.fullTokenAvailable ? (
                      <Badge status="success" text="Active" />
                    ) : (
                      <Badge status="error" text="Not set" />
                    )}
                  </Space>
                </div>
                
                {config.lastUpdated && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: '#f8f9fa',
                    borderRadius: '12px'
                  }}>
                    <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Last Updated:</Text>
                    <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>
                      {new Date(config.lastUpdated).toLocaleString()}
                    </Text>
                  </div>
                )}
              </Space>
            </Spin>
          </Card>
        </Col>

        {/* Update Token Form */}
        <Col xs={24}>
          <Card 
            title={
              <Space>
                <SaveOutlined style={{ color: '#722ed1' }} />
                <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600 }}>
                  🔐 Update API Configuration
                </span>
              </Space>
            }
            style={{ 
              borderRadius: isMobile ? '16px' : '20px', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0'
            }}
            headStyle={{
              borderBottom: '1px solid #f0f0f0',
              padding: isMobile ? '16px 20px' : '20px 24px'
            }}
            bodyStyle={{
              padding: isMobile ? '20px' : '24px'
            }}
          >
            <Alert
              message="⚠️ Important Notice"
              description="Updating the API token will automatically restart the server to apply changes. All active connections will be temporarily disconnected."
              type="warning"
              icon={<WarningOutlined />}
              style={{ 
                marginBottom: '24px',
                borderRadius: '12px',
                fontSize: isMobile ? '14px' : '16px'
              }}
              showIcon
            />
            
            <Form
              form={form}
              layout="vertical"
              onFinish={updateToken}
              disabled={loading}
            >
              <Form.Item
                label={
                  <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 600 }}>
                    🔑 Upstox API Token
                  </span>
                }
                name="apiToken"
                rules={[
                  { required: true, message: 'Please enter API Token' },
                  { min: 50, message: 'Token seems too short' }
                ]}
              >
                <TextArea 
                  placeholder="Enter Upstox API Token (JWT)"
                  rows={isMobile ? 3 : 4}
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: isMobile ? '14px' : '12px',
                    borderRadius: '12px',
                    border: '2px solid #f0f0f0'
                  }}
                />
              </Form.Item>
              
              <Form.Item>
                <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    icon={<SaveOutlined />}
                    loading={loading}
                    size="large"
                    style={{
                      height: isMobile ? '48px' : '44px',
                      borderRadius: '12px',
                      fontSize: isMobile ? '16px' : '14px',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                      border: 'none',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    💾 Update & Restart Server
                  </Button>
                  
                  <Button 
                    onClick={() => form.resetFields()}
                    disabled={loading}
                    size="large"
                    style={{
                      height: isMobile ? '48px' : '44px',
                      borderRadius: '12px',
                      fontSize: isMobile ? '16px' : '14px',
                      fontWeight: 600,
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    🔄 Reset Form
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Instructions Card */}
     
      </Row>
    </div>
  );
}

export default Settings;
